import { logger } from './logger';

/**
 * JavaScript optimization utilities for LCP improvement
 */

// Defer non-critical JavaScript loading
export const deferNonCriticalJS = () => {
  // List of non-critical scripts to defer
  const nonCriticalScripts: string[] = [
    // Add any non-critical JS files here
    // These will be loaded after the page is fully loaded
  ];

  // Load scripts after page load
  const loadScript = (src: string, async = true, defer = true) => {
    return new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = async;
      script.defer = defer;

      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));

      document.head.appendChild(script);
    });
  };

  // Load non-critical scripts after page load
  window.addEventListener('load', () => {
    nonCriticalScripts.forEach((src) => {
      loadScript(src).catch((error) => {
        logger.warn('Failed to load non-critical script:', error);
      });
    });
  });
};

// Preload critical JavaScript modules
export const preloadCriticalJS = (scriptUrls: string[]) => {
  scriptUrls.forEach((url) => {
    const link = document.createElement('link');
    link.rel = 'modulepreload';
    link.href = url;
    document.head.appendChild(link);
  });
};

// Optimize third-party scripts
export const optimizeThirdPartyScripts = () => {
  // Defer Google Analytics and other analytics
  const deferAnalytics = () => {
    window.addEventListener('load', () => {
      // Load analytics after page load
      if (typeof (window as Window & { gtag?: unknown }).gtag === 'undefined') {
        // Analytics loading logic here if needed
        logger.log('Analytics deferred until after page load');
      }
    });
  };

  deferAnalytics();
};

// Code splitting helper for dynamic imports
export const loadComponentDynamically = async <T>(
  importFn: () => Promise<{ default: T }>,
): Promise<T> => {
  try {
    const module = await importFn();
    return module.default;
  } catch (error) {
    logger.error('Failed to load component dynamically:', error);
    throw error;
  }
};

// Intersection Observer for lazy loading components
export const createLazyLoader = (callback: () => void, options: IntersectionObserverInit = {}) => {
  const defaultOptions: IntersectionObserverInit = {
    root: null,
    rootMargin: '50px',
    threshold: 0.1,
    ...options,
  };

  return new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        callback();
      }
    });
  }, defaultOptions);
};

// Initialize JavaScript optimizations
export const initializeJSOptimizations = () => {
  // Defer non-critical JavaScript
  deferNonCriticalJS();

  // Optimize third-party scripts
  optimizeThirdPartyScripts();

  logger.log('✅ JavaScript optimizations initialized for LCP improvement');
};
